import { useSession } from "@golf-district/auth/nextjs-exports";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const STORAGE_KEY = "phoneGuest";

export const clearPhoneGuestSession = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Returns true when the current user is a phone-booking guest.
 *
 * On all pages except the confirmation page, this reads directly from the
 * live session — so regular users (authenticated or not) are never affected.
 *
 * On the confirmation page only, a sessionStorage latch is used so the
 * phone-guest UI stays hidden even after signOut() clears the session cookie.
 * The latch is cleaned up when the user navigates away.
 */
export const useIsPhoneGuest = (): boolean => {
  const session = useSession();
  const pathname = usePathname();
  const isConfirmationPage = !!pathname?.includes("/checkout/confirmation");

  const sessionIsPhoneGuest = !!session?.data?.user?.isPhoneGuest;

  // Latch used ONLY on the confirmation page to survive signOut()
  const [confirmationLatch, setConfirmationLatch] = useState(false);

  useEffect(() => {
    if (!isConfirmationPage) return;
    if (sessionIsPhoneGuest) {
      // Phone guest landed on confirmation page — latch it
      sessionStorage.setItem(STORAGE_KEY, "1");
      setConfirmationLatch(true);
    } else {
      // Read stale latch (session already cleared by signOut)
      setConfirmationLatch(sessionStorage.getItem(STORAGE_KEY) === "1");
    }
  }, [sessionIsPhoneGuest, isConfirmationPage]);

  // Clean up latch when navigating away from confirmation page
  useEffect(() => {
    if (!isConfirmationPage) {
      clearPhoneGuestSession();
      setConfirmationLatch(false);
    }
  }, [isConfirmationPage]);

  // On confirmation page: use the latch so the UI survives signOut
  // Everywhere else: use only the live session (regular users unaffected)
  return isConfirmationPage ? confirmationLatch : sessionIsPhoneGuest;
};
